import { NavLink, useParams } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  formatDate,
  IconArrowLeft,
  IconAttention,
  IconBlock,
  IconExpand,
  IconLoader,
  IconSelected,
  IconSend,
  PermissionDenied,
  PermissionEnum,
  Popup,
  Table,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { useEffect } from 'react';
import useDetails from '../hooks/use-details';
import { expandableColumns } from '../variables/eftr';
import AddTransactionsSidepanel from './add-transactions-sidepanel';
import { clearReportDetailsState, setReportId } from '../store/slices/report';

import ROUTES from '../../../routes';

import cn from '../styles.module.css';

const getReportStatusClassName = (status: string) => {
  let statusClassName;

  switch (status) {
    case 'SENT':
      statusClassName = cn.statusSent;
      break;
    case 'ACCEPTED':
      statusClassName = cn.statusAccepted;
      break;
    case 'REJECTED':
      statusClassName = cn.statusRejected;
      break;
    case 'CANCELLED':
      statusClassName = cn.statusCancelled;
      break;
    default:
      break;
  }

  return statusClassName;
};

const ReportDetails = () => {
  const { currencies, activePermissions } = useAppSelector(
    (state) => state.root,
  );
  const {
    report,
    reportTransactions,
    reportTransactionsPagination,
    selectedTransactions,
    reportId: storeReportId,
  } = useAppSelector((state) => state.fintrac.details);

  const { reportId } = useParams();

  const dispatch = useAppDispatch();

  const {
    onRowsSelect,
    formattedData,
    cancelReportVisible,
    setCancelReportVisible,
    submitReportVisible,
    setSubmitReportVisible,
    addTransactionsSidepanelVisible,
    setAddTransactionsSidepanelVisible,
    isFetching,
    onCheckStatus,
    requestInProgress,
    selectedTransactionId,
    setDeletePopupVisible,
    deletePopupVisible,
    deleteError,
    deleteInProgress,
    onDeleteReport,
    onSubmitReport,
    submitInProgress,
    onCancelReport,
    cancelInProgress,
    selectAllInProgress,
    deselectAllInProgress,
    transactionsIsLoading,
    transactionsIsFetching,
    onToggleAll,
    selectedReportTransferDate,
    setSelectedReportTransferDate,
    reportType,
  } = useDetails({
    reportId: storeReportId,
    transactions: reportTransactions,
    currencies,
    activePermissions,
    report,
  });

  useEffect(() => {
    if (reportId) {
      dispatch(setReportId(reportId));
    }
  }, [reportId]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      dispatch(clearReportDetailsState());
    };
  }, []);

  let content;

  if (
    activePermissions?.includes(PermissionEnum.REGULATORY_REPORT_FINTRAC_LIST)
  ) {
    let reportContent;

    if (!report) {
      reportContent = (
        <div className={cn.reportLoader}>
          <IconLoader />
        </div>
      );
    } else if (report) {
      reportContent = (
        <>
          <NavLink
            to={
              reportType === 'eftr' ? ROUTES.FINTRAC_EFTR : ROUTES.FINTRAC_LVCTR
            }
            className={cn.backLink}
          >
            <IconArrowLeft width={13} height={13} />
            Back
          </NavLink>
          <div className={cn.reportDetailsInfo}>
            <div className={cn.reportDetailsInfoTop}>
              <div className={cn.reportDetailsInfoTopLeft}>
                <div className={cn.reportDetailsInfoNameWrapper}>
                  <div
                    className={`${cn.reportDetailsStatus} ${getReportStatusClassName(report?.status)}`}
                  >{`Report ${report?.status.charAt(0)}${report?.status?.slice(1).toLowerCase()}`}</div>
                </div>
                <div className={cn.reportDetailsDates}>
                  <div
                    className={cn.reportDetailsDate}
                  >{`Created at: ${formatDate(report?.createdAt)}`}</div>
                  <div className={cn.squareWrapper}>
                    <div className={cn.square} />
                  </div>
                  <div
                    className={cn.reportDetailsDate}
                  >{`Deadline: ${formatDate(report?.deadlineAt as string)}`}</div>
                </div>
              </div>
              <div className={cn.reportDetailsInfoTopRight}>
                {!transactionsIsLoading &&
                  reportTransactions &&
                  reportTransactions?.length > 0 &&
                  (report?.status === 'CREATED' ||
                    report?.status === 'REJECTED' ||
                    report?.status === 'SENT') && (
                    <>
                      {report?.status === 'SENT' && (
                        <ButtonSecondary
                          className={`button-white ${cn.reportButton} ${cn.checkStatusButton}`}
                          text={isFetching ? 'Loading...' : 'Check status'}
                          size={ButtonSize.SMALL}
                          onClick={onCheckStatus}
                        />
                      )}
                      <ButtonSecondary
                        size={ButtonSize.SMALL}
                        text="Cancel report"
                        icon={<IconBlock className={cn.cancelReportIcon} />}
                        className={`${cn.reportButton} button-white ${cn.withMargin}`}
                        onClick={() => setCancelReportVisible(true)}
                      />
                      {report?.status !== 'REJECTED' && (
                        <ButtonSecondary
                          size={ButtonSize.SMALL}
                          text="Add transaction"
                          icon={<IconExpand className={cn.addReportIcon} />}
                          className={`${cn.reportButton} button-white ${cn.withMargin}`}
                          onClick={() => {
                            setAddTransactionsSidepanelVisible(true);
                            setSelectedReportTransferDate(report?.periodStart);
                          }}
                        />
                      )}
                      <ButtonPrimary
                        size={ButtonSize.SMALL}
                        text="Submit report"
                        icon={<IconSend />}
                        className={cn.reportButton}
                        onClick={() => {
                          setSubmitReportVisible(true);
                        }}
                        disabled={report.totalAmount < 10000}
                      />
                    </>
                  )}
              </div>
            </div>
            <div className={cn.separator} />
            <div className={cn.reportDetailsInfoBottom}>
              <div className={cn.reportDetailsInfoBottomLeft}>
                <div className={cn.amountInfo}>
                  {report?.status === 'CREATED' &&
                    (report.totalAmount > 10000 ? (
                      <IconSelected className={cn.iconSelected} />
                    ) : (
                      <IconAttention
                        width={18}
                        height={18}
                        className={cn.iconAttention}
                      />
                    ))}
                  <div className={cn.amountValue}>
                    {formatBalance(
                      report?.totalAmount,
                      currencies?.find((item) => item.name === 'CAD'),
                    )}
                  </div>
                  <div className={cn.requiredAmount}>/&nbsp; 10000.00 CAD</div>
                </div>
              </div>

              <div className={cn.reportDetailsInfoBottomRight}>
                {report &&
                  report?.totalAmount < 10000 &&
                  report.status === 'CREATED' && (
                    <div className={cn.amountError}>
                      The total value of the transactions must be at least
                      10,000 CAD
                    </div>
                  )}
                {(report?.status === 'CREATED' ||
                  report?.status === 'CANCELLED') && (
                  <ButtonSecondary
                    className={`button-white button-warning ${cn.deleteReportButton}`}
                    size={ButtonSize.SMALL}
                    onClick={() => setDeletePopupVisible(true)}
                    text="Delete report"
                  />
                )}
              </div>
            </div>
          </div>

          <div className={cn.reportDetailsTableWrapper}>
            {!transactionsIsLoading && !reportTransactions?.length && (
              <div className={cn.emptyTransactions}>
                <div className={cn.emptyTransactionsTitle}>
                  Add transactions totalling at least 10,000 CAD
                </div>
                {report.status === 'CREATED' && (
                  <div className={cn.emptyTransactionsButtonWrapper}>
                    <ButtonSecondary
                      size={ButtonSize.SMALL}
                      text="Add transaction"
                      icon={<IconExpand className={cn.addReportIcon} />}
                      className={`${cn.reportButton} button-white ${cn.withMargin}`}
                      onClick={() => {
                        setAddTransactionsSidepanelVisible(true);
                        setSelectedReportTransferDate(report.periodStart);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {!transactionsIsLoading &&
              reportTransactions &&
              reportTransactions?.length > 0 && (
                <Table
                  columns={expandableColumns}
                  data={formattedData || []}
                  onSort={() => {}}
                  filters={{}}
                  sort={{}}
                  columnsLabels={{}}
                  onFilterChange={() => {}}
                  pagination={reportTransactionsPagination}
                  isLoading={transactionsIsLoading || transactionsIsFetching}
                  take="20"
                  setPage={() => {}}
                  onRowsSelect={(ids, id) => onRowsSelect(ids, id)}
                  tableFormClassName={cn.transactionsTable}
                  tableTopClassName={cn.transactionsTableTop}
                  rowsSelectDisabled={
                    (report?.status !== 'CREATED' &&
                      report?.status !== 'REJECTED') ||
                    deselectAllInProgress ||
                    selectAllInProgress
                  }
                  disabledRowIds={
                    requestInProgress && selectedTransactionId
                      ? [selectedTransactionId]
                      : []
                  }
                  onToggleAll={(toggle) => onToggleAll(toggle)}
                  selectedIds={selectedTransactions}
                  withScroll
                  withVerticalScroll
                  withToggleInput
                />
              )}
          </div>
        </>
      );
    }
    content = (
      <>
        {reportContent}
        <Popup
          isVisible={cancelReportVisible}
          onCancelClick={() => setCancelReportVisible(false)}
          onOkClick={onCancelReport}
          title="Cancel Report?"
          description="This report will not be submitted to FINTRAC. The transactions will be retained in the system."
          okButtonText={cancelInProgress ? 'Loading...' : 'Cancel report'}
          okButtonDisabled={cancelInProgress}
          cancelButtonText="Keep report"
        >
          <div>
            {deleteError && (
              <div className={cn.deleteReportError}>{deleteError}</div>
            )}
          </div>
        </Popup>
        <Popup
          isVisible={submitReportVisible}
          onCancelClick={() => setSubmitReportVisible(false)}
          onOkClick={onSubmitReport}
          title="Submit report?"
          description={`This report with total amount of ${report?.totalAmount} CAD will be submitted. Once sent, the report cannot be edited.`}
          okButtonText={submitInProgress ? 'Loading...' : 'Submit report'}
          okButtonDisabled={submitInProgress}
          cancelButtonText="Cancel"
        >
          <div>
            {deleteError && (
              <div className={cn.deleteReportError}>{deleteError}</div>
            )}
          </div>
        </Popup>
        <Popup
          isVisible={deletePopupVisible}
          onCancelClick={() => {
            setDeletePopupVisible(false);
          }}
          onOkClick={onDeleteReport}
          title="Are you sure you want to delete this report?"
          okButtonText={deleteInProgress ? 'Loading...' : 'Yes, Delete'}
          cancelButtonText="Cancel"
          okButtonDisabled={deleteInProgress}
        >
          <div>
            {deleteError && (
              <div className={cn.deleteReportError}>{deleteError}</div>
            )}
          </div>
        </Popup>
        <AddTransactionsSidepanel
          setAddTransactionsSidepanelVisible={
            setAddTransactionsSidepanelVisible
          }
          selectedReportTransferDate={selectedReportTransferDate}
          setSelectedReportTransferDate={setSelectedReportTransferDate}
          addTransactionsSidepanelVisible={addTransactionsSidepanelVisible}
          type={reportType as 'eftr' | 'lvctr'}
          selectedReportId={reportId}
        />
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return <div className={cn.reportDetails}>{content}</div>;
};

export default ReportDetails;
